import { useCallback, useEffect, useState } from "react"
import type { WorkerScenario } from "@/components/ats-fbo/types"

export interface UsePendingProfileTypeChangeProps {
  /** The currently committed profile type code on the form. Switching value triggers the
   *  effect that clears any stale prompt. */
  currentProfileTypeCode: string | null
  /** When non-null, a profile-type change is destructive (re-deriving the scenario can
   *  switch contacts or drop into add-profile, losing the prior selection). */
  workerScenario: WorkerScenario | null
  onProfileTypeChange: (code: string | null) => void
}

export interface UsePendingProfileTypeChangeReturn {
  /** Non-null while the inline confirmation is awaiting the user's decision. The trigger
   *  itself keeps showing the OLD value because the form hasn't been updated yet — the UI
   *  accurately reflects "nothing has changed yet" until confirm/cancel. */
  pendingProfileTypeCode: string | null
  /** True when committing a change would discard the current worker/org selection. */
  wouldDiscard: boolean
  /** Wired to the SearchableSelect's onValueChange: holds the change in pending state when
   *  it would discard, otherwise commits immediately. */
  attemptChange: (code: string | null) => void
  /** Wired to the prompt's "Yes, switch" button. */
  confirm: () => void
  /** Wired to the prompt's "Cancel" button. */
  cancel: () => void
}

/**
 * Intercepts profile-type changes that would discard the selected worker / organization
 * (because re-deriving the scenario can switch the contact or drop into add-profile mode).
 * Surfaces an inline confirmation panel so the user sees what they're about to lose. The
 * pending state auto-clears whenever the underlying profile type or scenario changes,
 * preventing a stale prompt from sitting around after the form moves on.
 */
export function usePendingProfileTypeChange({
  currentProfileTypeCode,
  workerScenario,
  onProfileTypeChange,
}: UsePendingProfileTypeChangeProps): UsePendingProfileTypeChangeReturn {
  const [pendingProfileTypeCode, setPendingProfileTypeCode] = useState<string | null>(null)

  // Always clear if the profile type updates (Yes path) or the worker scenario is reset
  // (e.g. email change), so a stale prompt can't linger.
  useEffect(() => {
    setPendingProfileTypeCode(null)
  }, [currentProfileTypeCode, workerScenario])

  const wouldDiscard = workerScenario != null

  const attemptChange = useCallback(
    (code: string | null) => {
      if (code !== null && code !== currentProfileTypeCode && wouldDiscard) {
        setPendingProfileTypeCode(code)
        return
      }
      setPendingProfileTypeCode(null)
      onProfileTypeChange(code)
    },
    [currentProfileTypeCode, wouldDiscard, onProfileTypeChange]
  )

  const confirm = useCallback(() => {
    setPendingProfileTypeCode((pending) => {
      if (pending != null) onProfileTypeChange(pending)
      return null
    })
  }, [onProfileTypeChange])

  const cancel = useCallback(() => {
    setPendingProfileTypeCode(null)
  }, [])

  return { pendingProfileTypeCode, wouldDiscard, attemptChange, confirm, cancel }
}
